import { call, leaderboardEnabled, playerId, playerName } from './leaderboard';
import type { Submission } from './submission';
import type { RoomStandingRow } from '../game/roomstandings';
import type { GrandPrixStage } from '../game/grandprix';

/**
 * Grand Prix room client.
 *
 * A room is just a short code plus membership — no live state. Everyone in it
 * drives the same Grand Prix stages solo, whenever they want, and standings
 * are computed server-side from everyone's verified times. See `server/src/
 * worker.ts`'s `/room/*` handlers.
 */

const KEY = 'karting-hotlap:room-code';

/** The room this browser last joined or created, or null outside one. */
export function roomCode(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setRoomCode(code: string): void {
  try {
    localStorage.setItem(KEY, code);
  } catch {
    /* ignore */
  }
}

/**
 * Forgets the room locally only. There's no room-owner concept, so removing
 * server-side membership would mean anyone could erase a friend's already
 * recorded times just by tapping Leave — this only stops showing it as
 * "your room" in this browser.
 */
export function clearRoomCode(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export interface RoomMember {
  /** Per-room pseudonymous handle — never the bearer `player` id, which the
   *  server keeps server-side only (see server/src/worker.ts's `toMemberId`). */
  memberId: string;
  name: string;
}

export interface RoomJoinResult {
  status: 'ok';
  code: string;
  members: RoomMember[];
}

export async function createRoom(): Promise<RoomJoinResult | null> {
  if (!leaderboardEnabled()) return null;
  const body = JSON.stringify({ player: playerId(), name: playerName() });
  const res = await call<RoomJoinResult & { status: string }>('/room/create', { method: 'POST', body });
  return res && res.status === 'ok' ? res : null;
}

export async function joinRoom(code: string): Promise<RoomJoinResult | { status: 'error'; message: string } | null> {
  if (!leaderboardEnabled()) return null;
  const body = JSON.stringify({ code, player: playerId(), name: playerName() });
  const res = await call<(RoomJoinResult | { status: 'error'; message: string }) & { status: string }>(
    '/room/join',
    { method: 'POST', body },
  );
  return res ?? { status: 'error', message: 'Could not reach the room' };
}

export async function submitRoomStage(
  code: string,
  submission: Submission,
): Promise<{ accepted: boolean; rank?: number; message?: string }> {
  if (!leaderboardEnabled()) return { accepted: false, message: 'No leaderboard configured' };
  const body = JSON.stringify({ ...submission, code, player: playerId(), name: playerName() });
  const res = await call<{ accepted: boolean; rank?: number; message?: string }>('/room/submit', {
    method: 'POST',
    body,
  });
  return res ?? { accepted: false, message: 'Submission failed' };
}

export interface RoomStandingsPage {
  status: 'ok' | 'error';
  code: string;
  stages: GrandPrixStage[];
  standings: (RoomStandingRow & { isYou: boolean })[];
  message?: string;
}

const EMPTY_STANDINGS: Omit<RoomStandingsPage, 'code'> = { status: 'error', stages: [], standings: [] };

export async function fetchRoomStandings(code: string, mode: string): Promise<RoomStandingsPage> {
  if (!leaderboardEnabled()) return { ...EMPTY_STANDINGS, code };
  const params = new URLSearchParams({ code, mode, player: playerId() });
  const page = await call<RoomStandingsPage>(`/room/standings?${params}`);
  return page ?? { ...EMPTY_STANDINGS, code, message: 'Standings unreachable' };
}
